// src/pages/Booking/ConfirmationPage.js
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBooking } from '../../contexts/BookingContext';
import { formatPriceFCFA } from '../../utils/formatters';
import { MapPin, Car, CheckCircle } from 'lucide-react';
import './ConfirmationPage.css';

const ConfirmationPage = () => {
  const navigate    = useNavigate();
  const location    = useLocation();
  const reservation = location.state?.reservation;

  const { selectedSalon, selectedServices, selectedDate, selectedTime, clearServices } = useBooking();

  const salonData    = selectedSalon;
  const servicesData = reservation?.services_details
    ? (typeof reservation.services_details === 'string'
        ? JSON.parse(reservation.services_details)
        : reservation.services_details)
    : selectedServices;
  const dateData      = reservation?.reservation_date || selectedDate;
  const timeData      = reservation?.reservation_time || selectedTime;
  const reservationId = reservation?.id;
  const clientNotes   = reservation?.client_notes || '';

  // Détecter si c'est un déplacement
  const isDeplacement = clientNotes.includes('DEPLACEMENT');
  const quartierMatch = clientNotes.match(/Quartier\s*:\s*([^\n|]+)/i);
  const quartier      = quartierMatch ? quartierMatch[1].trim() : null;
  const fraisMatch    = clientNotes.match(/Frais\s*:\s*([^\n|]+)/i);
  const frais         = fraisMatch ? fraisMatch[1].trim() : null;
  const paymentNumber = salonData?.wave_phone || salonData?.orange_phone || null;
  const paymentMethod = salonData?.wave_phone ? 'Wave' : salonData?.orange_phone ? 'Orange Money' : null;

  useEffect(() => {
    if (!salonData || !servicesData || servicesData.length === 0) {
      navigate('/');
    }
  }, [salonData, servicesData, navigate]);

  if (!salonData || !servicesData || servicesData.length === 0) return null;

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins  = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h${mins}`;
    if (hours > 0) return `${hours}h`;
    return `${mins}min`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Date invalide';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const totalDuration = servicesData.reduce((sum, s) => sum + (s.duration || 0), 0);
  const totalPrice    = servicesData.reduce((sum, s) => sum + parseFloat(s.price || 0), 0);

  const handleReturnHome = () => {
    clearServices();
    navigate('/');
  };

  return (
    <div className="confirmation-page">
      <div className="confirmation-container">

        {/* Animation succès */}
        <div className="success-animation">
          <div className="checkmark-circle">
            <div className="checkmark" />
          </div>
        </div>

        <h1 className="confirmation-title">⏳ En attente de validation</h1>
        <p className="confirmation-subtitle">
          Votre demande de réservation a été envoyée au salon
        </p>

        {/* Acompte payé — confirmation */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: '#F0FDF4', border: '1px solid #86EFAC',
          borderRadius: 10, padding: '12px 16px', marginBottom: 16,
          fontSize: 13, color: '#15803D',
        }}>
          <CheckCircle size={15} />
          <span>Acompte payé avec succès — votre réservation est confirmée</span>
        </div>

        {/* Carte récapitulatif */}
        <div className="confirmation-card">

          {/* Salon */}
          <div className="conf-section">
            <h3>📍 Salon</h3>
            <p className="salon-name">{salonData.name}</p>
            <p className="salon-address">{salonData.address}, {salonData.city}</p>
          </div>

          {/* Lieu de prestation */}
          <div className="conf-section">
            <h3>📍 Lieu de la prestation</h3>
            {isDeplacement ? (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                background: '#FFF8F0', border: '1px solid #FDE4C8',
                borderRadius: 8, padding: '10px 12px', marginTop: 6,
              }}>
                <Car size={16} color="#F97316" style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#C2600A' }}>À domicile</div>
                  {quartier && <div style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>Quartier : {quartier}</div>}
                  {frais && <div style={{ fontSize: 13, color: '#475569' }}>Frais de déplacement : {frais}</div>}
                </div>
              </div>
            ) : (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#F8FAFC', border: '1px solid #E2E8F0',
                borderRadius: 8, padding: '10px 12px', marginTop: 6,
              }}>
                <MapPin size={16} color="#64748B" />
                <span style={{ fontSize: 13, color: '#475569' }}>
                  Au salon — {salonData.address}, {salonData.city}
                </span>
              </div>
            )}
          </div>

          {/* Date & Heure */}
          <div className="conf-section">
            <h3>📅 Date et heure</h3>
            <p className="conf-datetime">
              <strong>{formatDate(dateData)}</strong>
              <span> à </span>
              <strong>{timeData}</strong>
            </p>
          </div>

          {/* Services */}
          <div className="conf-section">
            <h3>✂️ Prestations</h3>
            <ul className="services-list">
              {servicesData.map((service, index) => (
                <li key={index}>
                  <span>{service.name}</span>
                  <span className="service-price">{formatPriceFCFA(service.price)}</span>
                </li>
              ))}
            </ul>
            <div className="services-summary">
              <span>Durée totale : {formatDuration(totalDuration)}</span>
              <span className="total-price">Total : {formatPriceFCFA(totalPrice)}</span>
            </div>
          </div>

          {/* Numéro de réservation */}
          {reservationId && (
            <div className="conf-section">
              <p className="reservation-number">
                Numéro de réservation : <strong>#{reservationId.slice(0, 8)}</strong>
              </p>
            </div>
          )}

        </div>

        {/* Notice déplacement */}
        {isDeplacement && paymentNumber && (
          <div style={{
            background: '#FFF8F0',
            border: '1.5px solid #FDE4C8',
            borderLeft: '4px solid #F97316',
            borderRadius: 10,
            padding: '14px 16px',
            marginBottom: 16,
            fontSize: 13,
            color: '#92400E',
            lineHeight: 1.6,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 800, marginBottom: 8, color: '#C2600A' }}>
              <Car size={14} />
              Rappel — Prestation à domicile
            </div>
            <p style={{ margin: 0 }}>
              Dès confirmation de votre réservation, merci d'envoyer les frais de déplacement
              {frais ? ` (${frais})` : ''} au numéro {paymentMethod} : <strong>{paymentNumber}</strong>.
              Le salon vous contactera pour confirmer votre adresse exacte.
              Pensez à envoyer la preuve de votre virement au salon.
            </p>
          </div>
        )}

        {/* Instructions */}
        <div className="confirmation-instructions">
          <h3>📝 Informations importantes</h3>
          <ul>
            <li>✅ Le salon recevra votre demande et vous contactera pour confirmer</li>
            <li>⏰ Vous recevrez une notification une fois la réservation validée</li>
            <li>💬 Vous pourrez échanger avec le salon via la messagerie</li>
            {isDeplacement ? (
              <>
                <li>🚗 Le salon vous contactera pour confirmer votre adresse exacte</li>
                <li>
                  💸 Pensez à envoyer les frais de déplacement dès confirmation
                  {paymentNumber ? ` au ${paymentMethod} : ${paymentNumber}` : ''}
                </li>
                <li>📸 Conservez la preuve de votre virement et envoyez-la au salon</li>
              </>
            ) : (
              <li>📍 Rendez-vous directement au salon à l'heure prévue</li>
            )}
            <li>❌ En cas d'annulation, prévenir le salon au moins 24h avant</li>
          </ul>
        </div>

        {/* Actions */}
        <div className="confirmation-actions">
          <button className="btn-secondary" onClick={() => navigate('/reservations')}>
            📅 Voir mes réservations
          </button>
          <button className="btn-primary" onClick={handleReturnHome}>
            🏠 Retour à l'accueil
          </button>
        </div>

      </div>
    </div>
  );
};

export default ConfirmationPage;